import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Briefcase, Users, TrendingUp, MessageSquare, MapPin, Activity, BarChart3, ExternalLink, LineChart as LineChartIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getEducationLabel } from '../lib/constants';

const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

const PlatformAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [jobsHealth, setJobsHealth] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [activation, setActivation] = useState(null);
  const [categoryDemand, setCategoryDemand] = useState(null);
  const [messaging, setMessaging] = useState(null);
  const [growthLevers, setGrowthLevers] = useState(null);
  const [trends, setTrends] = useState(null);
  const [seekerInsights, setSeekerInsights] = useState(null);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchProfileDetails = async (userId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      const response = await api.get(`/api/profiles/${userId}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}`, 'X-Admin-View': 'true' }
      });
      setSelectedProfile(response.data.profile);
      setShowProfileModal(true);
    } catch (error) {
      toast.error('Failed to load profile');
      console.error('Profile fetch error:', error);
    }
  };

  const fetchAllAnalytics = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in as admin');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${session.session.access_token}` };

      const endpoints = [
        { name: 'jobs-health', call: api.get('/api/admin/analytics/jobs-health', { headers }) },
        { name: 'funnel', call: api.get('/api/admin/analytics/funnel', { headers }) },
        { name: 'employer-engagement', call: api.get('/api/admin/analytics/employer-engagement', { headers }) },
        { name: 'activation', call: api.get('/api/admin/analytics/activation', { headers }) },
        { name: 'category-demand', call: api.get('/api/admin/analytics/category-demand', { headers }) },
        { name: 'messaging', call: api.get('/api/admin/analytics/messaging', { headers }) },
        { name: 'growth-levers', call: api.get('/api/admin/analytics/growth-levers', { headers }) },
        { name: 'trends', call: api.get('/api/admin/analytics/trends', { headers }) },
        { name: 'seeker-insights', call: api.get('/api/admin/analytics/seeker-insights', { headers }) },
      ];

      const results = await Promise.allSettled(endpoints.map(e => e.call));

      if (results[0].status === 'fulfilled') setJobsHealth(results[0].value.data);
      if (results[1].status === 'fulfilled') setFunnel(results[1].value.data);
      if (results[2].status === 'fulfilled') setEngagement(results[2].value.data);
      if (results[3].status === 'fulfilled') setActivation(results[3].value.data);
      if (results[4].status === 'fulfilled') setCategoryDemand(results[4].value.data);
      if (results[5].status === 'fulfilled') setMessaging(results[5].value.data);
      if (results[6].status === 'fulfilled') setGrowthLevers(results[6].value.data);
      if (results[7].status === 'fulfilled') setTrends(results[7].value.data);
      if (results[8].status === 'fulfilled') setSeekerInsights(results[8].value.data);

      // Log failures
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`Failed to load ${endpoints[i].name}:`, r.reason);
        }
      });
    } catch (err) {
      console.error('Analytics fetch error:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl sm:text-3xl font-bold text-${color}-600`}>{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  const SectionHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-2 mb-4 mt-8">
      {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
      <h3 className="text-lg font-semibold">{title}</h3>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <h2 className="text-2xl font-bold">Platform Analytics</h2>
          <p className="text-muted-foreground">Loading analytics data...</p>
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse">
            <div className="h-8 bg-muted rounded w-48 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(j => (
                <div key={j} className="h-24 bg-muted rounded" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Funnel bars = strictly-nested, measurable on-platform steps only.
  // 'Profile Ready' is a quality gate most users skip (it isn't required to apply/
  // post), so it's surfaced as a separate stat — NOT a funnel step — to avoid a
  // misleading dip. 'hired'/'filled' are shown as self-reported outcomes (chips).
  const seekerFunnelData = funnel ? [
    { step: 'Signed Up', count: funnel.seeker.signed_up.count, pct: funnel.seeker.signed_up.pct },
    { step: 'Applied', count: funnel.seeker.applied.count, pct: funnel.seeker.applied.pct },
    { step: 'Viewed by Employer', count: funnel.seeker.viewed.count, pct: funnel.seeker.viewed.pct },
    { step: 'Got a Reply', count: funnel.seeker.replied.count, pct: funnel.seeker.replied.pct },
  ] : [];

  const employerFunnelData = funnel ? [
    { step: 'Signed Up', count: funnel.employer.signed_up.count, pct: funnel.employer.signed_up.pct },
    { step: 'Posted a Job', count: funnel.employer.posted.count, pct: funnel.employer.posted.pct },
    { step: 'Reviewed Applicants', count: funnel.employer.reviewed.count, pct: funnel.employer.reviewed.pct },
    { step: 'Contacted a Candidate', count: funnel.employer.contacted.count, pct: funnel.employer.contacted.pct },
  ] : [];

  // Prepare activation pie data
  const seekerPieData = activation ? [
    { name: 'Ready', value: activation.seeker.completeness.complete },
    { name: 'Partial', value: activation.seeker.completeness.partial },
    { name: 'Empty', value: activation.seeker.completeness.none },
  ] : [];

  const employerPieData = activation ? [
    { name: 'Ready', value: activation.employer.completeness.complete },
    { name: 'Partial', value: activation.employer.completeness.partial },
    { name: 'Empty', value: activation.employer.completeness.none },
  ] : [];

  // Category chart data — open_jobs now represents positions (accounts for positions_available)
  const categoryChartData = (categoryDemand?.categories || []).map(c => ({
    name: c.category.length > 15 ? c.category.substring(0, 15) + '...' : c.category,
    'Open Positions': c.open_jobs,
    'Matching Seekers': c.matching_seekers,
  }));

  // Geographic chart data
  const geoChartData = (growthLevers?.geographic || []).slice(0, 15).map(g => ({
    name: g.town.length > 12 ? g.town.substring(0, 12) + '...' : g.town,
    Jobs: g.jobs,
    Seekers: g.seekers,
  }));

  // Seeker coverage as % of all seekers
  const pctOf = (n) => seekerInsights?.total ? Math.round((n / seekerInsights.total) * 100) : 0;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Platform Analytics</h2>
        <p className="text-muted-foreground">Operational metrics, user behavior, and growth opportunities</p>
      </div>

      {/* ===== GROWTH TRENDS ===== */}
      <SectionHeader title="Growth Trends (last 12 months)" icon={LineChartIcon} />

      {trends?.data?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">New signups, jobs, applications &amp; messages by month</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="seekers" name="New Seekers" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="employers" name="New Employers" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="jobs" name="Jobs Posted" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="applications" name="Applications" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="messages" name="Messages" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ===== TIER 1: OPERATIONAL ===== */}

      {/* Section 1: Jobs & Application Health */}
      <SectionHeader title="Jobs & Application Health" icon={Briefcase} />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard
          title="Open Positions"
          value={jobsHealth?.open_positions ?? '-'}
          subtitle={jobsHealth ? `${jobsHealth.open_jobs} job posting${jobsHealth.open_jobs !== 1 ? 's' : ''}` : ''}
          icon={Briefcase}
        />
        <MetricCard
          title="With Applicants"
          value={jobsHealth?.with_applicants ?? '-'}
          subtitle={jobsHealth ? `${Math.round((jobsHealth.with_applicants / Math.max(jobsHealth.open_jobs, 1)) * 100)}% of open jobs` : ''}
          color="green"
        />
        <MetricCard
          title="Avg Apps/Job"
          value={jobsHealth?.avg_apps_per_job ?? '-'}
          icon={TrendingUp}
        />
        <Card className={(jobsHealth?.stale_jobs?.length || 0) > 0 ? 'border-orange-300 bg-orange-50' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Stale (7d+, 0 apps)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${(jobsHealth?.stale_jobs?.length || 0) > 0 ? 'text-orange-600' : ''}`}>
              {jobsHealth?.stale_jobs?.length ?? '-'}
            </div>
          </CardContent>
        </Card>
        <MetricCard
          title="Expired Unfilled"
          value={jobsHealth?.expired_count ?? '-'}
          subtitle={jobsHealth ? `${jobsHealth.fill_rate}% fill rate · ${jobsHealth.filled_count} filled` : ''}
          icon={AlertTriangle}
          color="orange"
        />
      </div>

      {/* Stale Jobs Table */}
      {jobsHealth?.stale_jobs?.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Stale Jobs — Open 7+ days with 0 applications</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Employer</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Days Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobsHealth.stale_jobs.slice(0, 15).map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <a
                        href={`/jobs/${job.id}`}
                        className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {job.title}
                        <ExternalLink size={12} />
                      </a>
                    </TableCell>
                    <TableCell
                      className="text-sm font-medium text-primary cursor-pointer hover:underline"
                      onClick={() => fetchProfileDetails(job.employer_id)}
                    >
                      {job.employer_name || 'Unknown'}
                    </TableCell>
                    <TableCell>{job.location}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={job.days_open > 14 ? 'destructive' : 'secondary'}>
                        {job.days_open}d
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Section 2: User Funnel */}
      <SectionHeader title="User Funnel" icon={TrendingUp} />

      {funnel && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Seeker Funnel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Seeker Pipeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {seekerFunnelData.map((step, i) => (
                <div key={step.step}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{step.step}</span>
                    <span className="text-muted-foreground">{step.count.toLocaleString()} ({step.pct}%)</span>
                  </div>
                  <Progress value={step.pct} className="h-3" />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Employer Funnel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Employer Pipeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {employerFunnelData.map((step, i) => (
                <div key={step.step}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{step.step}</span>
                    <span className="text-muted-foreground">{step.count.toLocaleString()} ({step.pct}%)</span>
                  </div>
                  <Progress value={step.pct} className="h-3" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {funnel && (
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>
            Profile completion (not required to apply/post):{' '}
            <span className="font-medium text-foreground">{funnel.seeker.profile_ready.pct}% of seekers</span> ({funnel.seeker.profile_ready.count})
            {' · '}
            <span className="font-medium text-foreground">{funnel.employer.profile_ready.pct}% of employers</span> ({funnel.employer.profile_ready.count}) have a complete profile
          </p>
          <p>
            Self-reported outcomes (under-counted — most hiring is finalized off-platform):{' '}
            <span className="font-medium text-foreground">{funnel.seeker.outcome_hired.count} seeker{funnel.seeker.outcome_hired.count !== 1 ? 's' : ''} marked hired</span>
            {' · '}
            <span className="font-medium text-foreground">{funnel.employer.outcome_filled.count} employer{funnel.employer.outcome_filled.count !== 1 ? 's' : ''} with a filled job</span>
          </p>
        </div>
      )}

      {/* Section 3: Employer Engagement */}
      <SectionHeader title="Employer Engagement" icon={Activity} />

      {engagement && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <MetricCard
              title="Avg View Rate"
              value={`${engagement.averages.avg_view_rate}%`}
              color="blue"
            />
            <MetricCard
              title="Avg Response Time"
              value={engagement.averages.avg_response_hours > 0 ? `${engagement.averages.avg_response_hours}h` : 'N/A'}
              color="green"
            />
            <MetricCard
              title="Avg Viewed"
              value={engagement.averages.avg_applicants_viewed}
              color="purple"
            />
            <MetricCard
              title="Avg Messages"
              value={engagement.averages.avg_messages}
              color="blue"
            />
            <MetricCard
              title="Avg Activity"
              value={engagement.averages.avg_activity}
              color="green"
            />
          </div>

          {engagement.employers.length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Employer Engagement Rankings</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employer</TableHead>
                      <TableHead>Town</TableHead>
                      <TableHead className="text-right">View Rate</TableHead>
                      <TableHead className="text-right">Response</TableHead>
                      <TableHead className="text-right">Viewed</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Activity</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {engagement.employers.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell
                          className="font-medium text-primary cursor-pointer hover:underline"
                          onClick={() => fetchProfileDetails(emp.id)}
                        >
                          {emp.name}
                        </TableCell>
                        <TableCell>{emp.town}</TableCell>
                        <TableCell className="text-right">{emp.view_rate}%</TableCell>
                        <TableCell className="text-right">
                          {emp.avg_response_hours != null ? `${emp.avg_response_hours}h` : '-'}
                        </TableCell>
                        <TableCell className="text-right">{emp.applicants_viewed}</TableCell>
                        <TableCell className="text-right">{emp.messages_sent}</TableCell>
                        <TableCell className="text-right">{emp.activity_count ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={emp.score >= 50 ? 'default' : 'secondary'}
                            className={emp.score >= 70 ? 'bg-green-500' : emp.score >= 40 ? 'bg-blue-500' : ''}>
                            {emp.score}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== TIER 2: USER BEHAVIOR ===== */}

      {/* Section 4: Activation & Profile Health */}
      <SectionHeader title="Activation & Profile Health" icon={Users} />

      {activation && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Seeker Completeness */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Seeker Profile Richness <span className="font-normal text-muted-foreground">(Ready = bio + skills)</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={seekerPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {seekerPieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-sm text-center space-y-1 mt-2">
                <p>Total: {activation.seeker.total} seekers</p>
                <p>Took action: {activation.seeker.first_action.any_action} ({activation.seeker.total > 0 ? Math.round((activation.seeker.first_action.any_action / activation.seeker.total) * 100) : 0}%)</p>
                <p className="text-muted-foreground">Applied: {activation.seeker.first_action.applied} | Saved: {activation.seeker.first_action.saved} | Messaged: {activation.seeker.first_action.messaged}</p>
              </div>
            </CardContent>
          </Card>

          {/* Employer Completeness */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Employer Profile Richness <span className="font-normal text-muted-foreground">(Ready = business + bio)</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={employerPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {employerPieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-sm text-center space-y-1 mt-2">
                <p>Total: {activation.employer.total} employers</p>
                <p>Took action: {activation.employer.first_action.any_action} ({activation.employer.total > 0 ? Math.round((activation.employer.first_action.any_action / activation.employer.total) * 100) : 0}%)</p>
                <p className="text-muted-foreground">Posted: {activation.employer.first_action.posted} | Messaged: {activation.employer.first_action.messaged}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section: Job Seeker Insights */}
      <SectionHeader title="Job Seeker Insights" icon={Users} />

      {seekerInsights && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <MetricCard
              title="Total Seekers"
              value={(seekerInsights.total || 0).toLocaleString()}
              icon={Users}
            />
            <MetricCard
              title="Has Phone"
              value={`${pctOf(seekerInsights.coverage.has_phone)}%`}
              subtitle={`${seekerInsights.coverage.has_phone.toLocaleString()} reachable by SMS`}
              color="green"
            />
            <MetricCard
              title="Has Skills"
              value={`${pctOf(seekerInsights.coverage.has_skills)}%`}
              color="blue"
            />
            <MetricCard
              title="Verified"
              value={`${pctOf(seekerInsights.coverage.verified)}%`}
              color="purple"
            />
            <MetricCard
              title="Has Bio"
              value={`${pctOf(seekerInsights.coverage.has_bio)}%`}
              color="blue"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Top Towns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={seekerInsights.towns} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="town" width={95} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Seekers" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={seekerInsights.skills} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="skill" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Seekers" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Years of Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={seekerInsights.experience} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="level" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={55} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="Seekers" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Education Level</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={seekerInsights.education} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="level" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={55} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="Seekers" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Section 5: Category Intelligence */}
      <SectionHeader title="Category Intelligence" icon={BarChart3} />

      {categoryDemand && (
        <>
          {categoryChartData.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Open Jobs vs Matching Seekers by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categoryChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Open Positions" fill="#3b82f6" />
                    <Bar dataKey="Matching Seekers" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Gap Analysis</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Seekers = all registered job seekers whose skills, education, work history or
                past applications match the category, so one seeker can count in several.
                Postings and positions are what is open right now.
              </p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Postings</TableHead>
                    <TableHead className="text-right">Positions</TableHead>
                    <TableHead className="text-right">Seekers</TableHead>
                    <TableHead className="text-right">Gap</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryDemand.categories.map((cat) => (
                    <TableRow key={cat.category_id}>
                      <TableCell className="font-medium">{cat.category}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{cat.job_postings ?? '-'}</TableCell>
                      <TableCell className="text-right">{cat.open_jobs}</TableCell>
                      <TableCell className="text-right">{cat.matching_seekers}</TableCell>
                      <TableCell className="text-right">{cat.gap > 0 ? `+${cat.gap}` : cat.gap}</TableCell>
                      <TableCell>
                        <Badge variant={cat.status === 'undersupply' ? 'destructive' : cat.status === 'oversupply' ? 'secondary' : 'default'}
                          className={cat.status === 'balanced' ? 'bg-green-500'
                            : cat.status === 'no openings' ? 'bg-slate-100 text-slate-500 hover:bg-slate-100' : ''}>
                          {cat.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Section 6: Response & Messaging */}
      <SectionHeader title="Response & Messaging" icon={MessageSquare} />

      {messaging && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricCard
            title="Apps Viewed"
            value={`${messaging.applications.viewed} / ${messaging.applications.total}`}
            subtitle={`${messaging.applications.view_rate}% view rate`}
          />
          <MetricCard
            title="Avg Time to View"
            value={messaging.applications.avg_hours_to_view > 0 ? `${messaging.applications.avg_hours_to_view}h` : 'N/A'}
            color="green"
          />
          <MetricCard
            title="Median Time to View"
            value={messaging.applications.median_hours_to_view > 0 ? `${messaging.applications.median_hours_to_view}h` : 'N/A'}
            color="green"
          />
          <MetricCard
            title="Total Messages"
            value={messaging.messaging.total_messages.toLocaleString()}
            subtitle={`${messaging.messaging.total_threads} threads`}
          />
          <MetricCard
            title="Employer Started Threads"
            value={`${messaging.messaging.employer_initiated_threads_pct}%`}
            subtitle={`Seeker started: ${messaging.messaging.seeker_initiated_threads_pct}%`}
            color="purple"
          />
          <MetricCard
            title="Msgs from Employers"
            value={`${messaging.messaging.employer_msg_volume_pct}%`}
            subtitle={`Seeker: ${messaging.messaging.seeker_msg_volume_pct}%`}
            color="purple"
          />
          <MetricCard
            title="Read Rate"
            value={`${messaging.messaging.read_rate}%`}
            subtitle={`Avg ${messaging.messaging.avg_thread_length} msgs/thread`}
            color="blue"
          />
        </div>
      )}

      {/* ===== TIER 3: GROWTH LEVERS ===== */}

      {/* Section 7: Growth Opportunities */}
      <SectionHeader title="Growth Opportunities" icon={TrendingUp} />

      {growthLevers && (
        <>
          {/* Ghost Seekers Summary */}
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Ghost Seekers — Signed up, never applied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{growthLevers.ghost_seekers.total}</div>
                  <p className="text-xs text-muted-foreground">{growthLevers.ghost_seekers.pct}% of all seekers</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{growthLevers.ghost_seekers.with_skills}</div>
                  <p className="text-xs text-muted-foreground">Have skills listed</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{growthLevers.ghost_seekers.without_skills}</div>
                  <p className="text-xs text-muted-foreground">No skills listed</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">{growthLevers.dormant_employers_total}</div>
                  <p className="text-xs text-muted-foreground">Dormant employers</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dormant Employers Table */}
          {growthLevers.dormant_employers.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Dormant Employers — Signed up, never posted</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Town</TableHead>
                      <TableHead className="text-right">Days Since Signup</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {growthLevers.dormant_employers.slice(0, 15).map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell
                          className="font-medium text-primary cursor-pointer hover:underline"
                          onClick={() => fetchProfileDetails(emp.id)}
                        >
                          {emp.name}
                        </TableCell>
                        <TableCell>{emp.business_name || '-'}</TableCell>
                        <TableCell>{emp.town}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={emp.days_since_signup > 30 ? 'destructive' : 'secondary'}>
                            {emp.days_since_signup}d
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Geographic Chart */}
          {geoChartData.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Geographic: Jobs vs Seekers by Town
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={geoChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Jobs" fill="#3b82f6" />
                    <Bar dataKey="Seekers" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
          </DialogHeader>
          {selectedProfile && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <Avatar className="w-20 h-20">
                  {selectedProfile.avatar_url ? (
                    <img src={selectedProfile.avatar_url} alt={selectedProfile.name} className="w-full h-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-2xl">{selectedProfile.name?.[0]}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedProfile.name || 'No name'}</h3>
                  <p className="text-sm text-muted-foreground">{selectedProfile.email || 'No email'}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="secondary">{selectedProfile.role || 'Unknown role'}</Badge>
                    {selectedProfile.verified && <Badge className="bg-green-500">Verified</Badge>}
                    {selectedProfile.banned && <Badge variant="destructive">Banned</Badge>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="text-xs font-mono break-all">{selectedProfile.id}</p>
                </div>
              </div>

              {/* Basic Info */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Basic Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Phone</p>
                    <p className="text-sm font-medium">{selectedProfile.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Town/Location</p>
                    <p className="text-sm font-medium">{selectedProfile.town || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Rating</p>
                    <p className="text-sm font-medium">{(selectedProfile.rating || 0).toFixed(1)} ({selectedProfile.review_count || 0} reviews)</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Credits Balance</p>
                    <p className="text-sm font-medium">{selectedProfile.credits || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Created</p>
                    <p className="text-sm font-medium">{selectedProfile.created_at ? new Date(selectedProfile.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Referral Code</p>
                    <p className="text-sm font-mono">{selectedProfile.referral_code || 'None'}</p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {(selectedProfile.bio || selectedProfile.tagline) && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Bio & Tagline</h4>
                  <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                    {selectedProfile.tagline && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Tagline</p>
                        <p className="text-sm italic">"{selectedProfile.tagline}"</p>
                      </div>
                    )}
                    {selectedProfile.bio && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Bio</p>
                        <p className="text-sm whitespace-pre-wrap">{selectedProfile.bio}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Job Seeker Details */}
              {(selectedProfile.role === 'job_seeker' || selectedProfile.role === 'contractor') && (
                <>
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Job Seeker Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Experience</p>
                        <p className="text-sm font-medium">{selectedProfile.years_of_experience || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Education</p>
                        <p className="text-sm font-medium">{getEducationLabel(selectedProfile.education_level) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Availability</p>
                        <p className="text-sm font-medium">{selectedProfile.availability || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  {selectedProfile.skills && selectedProfile.skills.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.skills.map((skill, i) => (
                          <Badge key={i} variant="outline">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Employer Details */}
              {(selectedProfile.role === 'employer' || selectedProfile.role === 'owner') && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Business Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Name</p>
                      <p className="text-sm font-medium">{selectedProfile.business_name || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Type</p>
                      <p className="text-sm font-medium">{selectedProfile.business_type || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Industry</p>
                      <p className="text-sm font-medium">{selectedProfile.industry || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Employees</p>
                      <p className="text-sm font-medium">{selectedProfile.number_of_employees || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Years in Business</p>
                      <p className="text-sm font-medium">{selectedProfile.years_in_business || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Website</p>
                      <p className="text-sm font-medium">
                        {selectedProfile.website ? (
                          <a href={selectedProfile.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {selectedProfile.website}
                          </a>
                        ) : 'Not provided'}
                      </p>
                    </div>
                  </div>
                  {selectedProfile.business_description && (
                    <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Business Description</p>
                      <p className="text-sm whitespace-pre-wrap">{selectedProfile.business_description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Data */}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View Raw Profile Data</summary>
                <pre className="mt-2 p-3 bg-muted rounded-lg overflow-x-auto text-xs">
                  {JSON.stringify(selectedProfile, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAnalytics;
